<template>
  <div class="web-courses-page">
    <!-- Custom Bundle Modal -->
    <div
      class="modal fade"
      id="customBundleModal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="customBundleModalLabel"
      aria-hidden="true"
      ref="customBundleModal"
    >
      <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document"
      >
        <div class="modal-content custom-bundle-modal">
          <div class="modal-header">
            <h5 class="modal-title" id="customBundleModalLabel">
              <span v-if="bundleStep === 1">{{
                $t("category_courses.available_courses") || "الدورات المتاحة"
              }}</span>
              <span v-else>{{
                $t("category_courses.selected_courses_review") ||
                "مراجعة الدورات المختارة"
              }}</span>
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              @click="resetBundleStep"
            ></button>
          </div>

          <!-- Step 1: Course Selection -->
          <div v-if="bundleStep === 1" class="modal-body">
            <!-- Loading State -->
            <div v-if="isLoadingCustomCourses" class="custom-bundle-loading">
              <div class="spinner"></div>
              <p>{{ $t("common.loading") || "جاري التحميل..." }}</p>
            </div>

            <!-- No Courses -->
            <div
              v-else-if="customCourses.length === 0"
              class="custom-bundle-empty"
            >
              <i class="fas fa-box-open"></i>
              <p>
                {{ $t("category_courses.no_courses") || "لا توجد دورات متاحة" }}
              </p>
            </div>

            <!-- Courses Grid -->
            <div v-else class="custom-bundle-grid">
              <div
                v-for="course in customCourses"
                :key="course.id"
                class="custom-bundle-card"
                :class="{ selected: selectedBundleCourses.includes(course.id) }"
              >
                <div class="bundle-card-image">
                  <img :src="course.image" @error="setDefaultImage" alt="" />
                </div>
                <div class="bundle-card-content">
                  <h4 class="bundle-card-title">{{ course.title }}</h4>
                  <p class="bundle-card-instructor">
                    {{ course.user?.fname }} {{ course.user?.lname }}
                  </p>
                  <div class="bundle-card-price">
                    <template
                      v-if="
                        (course.price === null || course.price === 0) &&
                        (course.discount_price === null || course.discount_price === 0)
                      "
                    >
                      <span class="price-free">{{
                        $t("course.free") || "مجاني"
                      }}</span>
                    </template>
                    <template v-else-if="course.discount_price">
                      <span class="price-current"
                        >{{ course.discount_price }} {{ $appCurrency }}</span
                      >
                      <span class="price-old"
                        >{{ course.price }} {{ $appCurrency }}</span
                      >
                    </template>
                    <template v-else>
                      <span class="price-current"
                        >{{ course.price }} {{ $appCurrency }}</span
                      >
                    </template>
                  </div>
                  <button
                    v-if="!selectedBundleCourses.includes(course.id)"
                    class="bundle-add-btn"
                    @click="addToBundleSelection(course.id)"
                  >
                    <i class="fas fa-plus"></i>
                    {{ $t("category_courses.add_to_bundle") || "إضافة للباقة" }}
                  </button>
                  <button
                    v-else
                    class="bundle-remove-btn"
                    @click="removeFromBundleSelection(course.id)"
                  >
                    <i class="fas fa-minus"></i>
                    {{
                      $t("category_courses.remove_from_bundle") ||
                      "إزالة من الباقة"
                    }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div
              v-if="
                customCourseLinks &&
                customCourseLinks.links &&
                customCourseLinks.links.length > 3
              "
              class="custom-bundle-pagination"
            >
              <button
                class="pagination-btn"
                :disabled="!customCourseLinks.links[0].url"
                @click="
                  fetchCustomBundleCourses(customCourseLinks.links[0].page)
                "
              >
                <i class="fas fa-chevron-right"></i>
              </button>
              <span class="page-info"
                >{{ currentCustomPage }} /
                {{ customCourseLinks.last_page || 1 }}</span
              >
              <button
                class="pagination-btn"
                :disabled="
                  !customCourseLinks.links[customCourseLinks.links.length - 1]
                    .url
                "
                @click="
                  fetchCustomBundleCourses(
                    customCourseLinks.links[customCourseLinks.links.length - 1]
                      .page
                  )
                "
              >
                <i class="fas fa-chevron-left"></i>
              </button>
            </div>
          </div>

          <!-- Step 1 Footer: Show Courses & Continue -->
          <div
            v-if="bundleStep === 1 && customCourses.length > 0"
            class="modal-footer custom-bundle-footer"
          >
            <div class="bundle-summary">
              <div class="summary-row">
                <span
                  >{{
                    $t("category_courses.selected_courses") ||
                    "الدورات المختارة"
                  }}:</span
                >
                <span class="summary-value">{{
                  selectedBundleCourses.length
                }}</span>
              </div>
              <div class="summary-row total">
                <span
                  >{{ $t("category_courses.total_price") || "الإجمالي" }}:</span
                >
                <span class="summary-value"
                  >{{ bundleBasePrice }} {{ $appCurrency }}</span
                >
              </div>
            </div>
            <button
              class="bundle-continue-btn"
              :disabled="selectedBundleCourses.length < bundleMinCourses"
              @click="goToStep2"
            >
              <i class="fas fa-eye"></i>
              {{
                $t("category_courses.show_courses_continue") ||
                "عرض الدورات والمتابعة"
              }}
            </button>
            <p
              v-if="selectedBundleCourses.length < bundleMinCourses"
              class="min-courses-hint"
            >
              {{
                $t("category_courses.min_courses_hint", {
                  min: bundleMinCourses,
                }) || `يجب اختيار ${bundleMinCourses} دورات على الأقل`
              }}
            </p>
          </div>

          <!-- Step 2: Review Selected Courses -->
          <div v-if="bundleStep === 2" class="modal-body bundle-review-body">
            <div class="bundle-review-header">
              <button class="back-btn" @click="goToStep1">
                <i class="fas fa-arrow-right"></i>
                {{ $t("common.back") || "رجوع" }}
              </button>
            </div>

            <!-- Selected Courses List -->
            <div class="selected-courses-list">
              <div
                v-for="course in selectedCoursesDetails"
                :key="course.id"
                class="selected-course-item"
              >
                <div class="selected-course-image">
                  <img
                    :src="course.image || course.preview_image || defaultImage"
                    :alt="course.title"
                    @error="setDefaultImage"
                  />
                </div>
                <div class="selected-course-info">
                  <h4 class="selected-course-title">{{ course.title }}</h4>
                  <p class="selected-course-instructor">
                    {{ course.user?.fname }} {{ course.user?.lname }}
                  </p>
                </div>
                <div class="selected-course-price">
                  {{ course.discount_price || course.price || 0 }}
                  {{ $appCurrency }}
                </div>
                <button
                  class="remove-course-btn"
                  @click="removeFromBundleSelection(course.id)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <!-- Order Summary -->
            <div class="bundle-order-summary">
              <h4 class="order-summary-title">
                <i class="fas fa-receipt"></i>
                {{ $t("custom_bundle.order_details") || "تفاصيل الطلب" }}
              </h4>
              <div class="order-summary-rows">
                <div class="order-row">
                  <span>{{
                    $t("category_courses.selected_courses") ||
                    "الدورات المختارة"
                  }}</span>
                  <span>{{ selectedBundleCourses.length }}</span>
                </div>
                <div class="order-row">
                  <span>{{
                    $t("category_courses.total_price") || "السعر الأساسي"
                  }}</span>
                  <span>{{ bundleBasePrice }} {{ $appCurrency }}</span>
                </div>
                <div class="order-row discount">
                  <span>{{ $t("category_courses.discount") || "الخصم" }}</span>
                  <span>{{ bundleDiscount }}%</span>
                </div>
                <div class="order-row total">
                  <span>{{
                    $t("category_courses.total_after_discount") ||
                    "السعر النهائي"
                  }}</span>
                  <span
                    >{{ bundleTotalPrice.toFixed(2) }} {{ $appCurrency }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2 Footer: Buy Now -->
          <div
            v-if="bundleStep === 2"
            class="modal-footer custom-bundle-footer"
          >
            <button
              class="bundle-checkout-btn"
              :disabled="
                isEnrollingBundle ||
                selectedBundleCourses.length < bundleMinCourses
              "
              @click="confirmBundleEnroll"
            >
              <span v-if="isEnrollingBundle">
                <i class="fas fa-spinner fa-spin"></i>
                {{ $t("common.please_wait") || "انتظر..." }}
              </span>
              <span v-else>
                <i class="fas fa-shopping-cart"></i>
                {{ $t("common.buy_now") || "اشتري الآن" }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== SPECIALTIES VIEW ===== -->
    <div v-if="!selectedChildCategory" class="specialties-view">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-container">
          <div class="hero-content">
            <h1 class="hero-title">
              {{ $t("web_courses.explore_specialties") || "استكشف التخصصات" }}
            </h1>
            <p class="hero-subtitle">
              {{
                $t("web_courses.hero_subtitle") ||
                "اكتشف مجموعة واسعة من الدورات التعليمية"
              }}
            </p>
            <div class="hero-search">
              <div class="search-wrapper">
                <i class="fas fa-search search-icon"></i>
                <input
                  v-model="globalSearchText"
                  type="text"
                  class="search-input"
                  :placeholder="
                    $t('web_courses.search_all_courses') ||
                    'ابحث في جميع الدورات...'
                  "
                  @input="debouncedGlobalSearch"
                />
                <button
                  v-if="globalSearchText"
                  class="search-clear-btn"
                  @click="clearGlobalSearch"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="hero-decoration">
            <div class="decoration-circle circle-1"></div>
            <div class="decoration-circle circle-2"></div>
          </div>
        </div>
      </section>

      <!-- Global Search Results Section -->
      <section
        v-if="showGlobalSearchResults"
        class="global-search-results-section"
      >
        <div class="section-container">
          <div class="search-results-header">
            <h2 class="section-title">
              <i class="fas fa-search"></i>
              {{ $t("web_courses.search_results") || "نتائج البحث" }}
              <span v-if="globalSearchQuery" class="search-query-text"
                >"{{ globalSearchQuery }}"</span
              >
            </h2>
            <button class="back-to-categories-btn" @click="clearGlobalSearch">
              <i class="fas fa-arrow-right"></i>
              {{ $t("web_courses.back_to_categories") || "العودة للتخصصات" }}
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="isLoadingGlobalSearch" class="loading-state">
            <div class="loading-grid">
              <div v-for="n in 6" :key="n" class="skeleton-card"></div>
            </div>
          </div>

          <!-- Search Results Grid -->
          <div
            v-else-if="globalSearchResults.length > 0"
            class="courses-grid global-search-grid"
          >
            <div
              v-for="course in globalSearchResults"
              :key="course.id"
              class="course-card"
              @click="navigateToGlobalSearchCourse(course)"
            >
              <div class="card-image">
                <img
                  :src="course.image || course.preview_image || defaultImage"
                  @error="setDefaultImage"
                  alt=""
                />
                <div v-if="course.is_purchased" class="purchased-badge">
                  <i class="fas fa-check-circle"></i>
                  {{ $t("web_courses.purchased") || "مشترك" }}
                </div>
                <div
                  v-if="!course.is_purchased && getItemPrice(course) > 0"
                  class="card-actions"
                >
                  <button
                    class="cart-icon-btn"
                    :class="{ 'in-cart': cartCourses.includes(course.id) }"
                    :disabled="loadingCart[course.id]"
                    @click.stop="addGlobalSearchCourseToCart(course)"
                  >
                    <i
                      v-if="loadingCart[course.id]"
                      class="fas fa-spinner fa-spin"
                    ></i>
                    <i
                      v-else-if="cartCourses.includes(course.id)"
                      class="fas fa-shopping-cart"
                    ></i>
                    <i v-else class="fas fa-cart-plus"></i>
                  </button>
                </div>
              </div>
              <div class="card-content">
                <h3 class="card-title">{{ course.title }}</h3>
                <p class="card-instructor">
                  {{ course.user?.fname }} {{ course.user?.lname }}
                </p>
                <div class="card-meta">
                  <span
                    v-if="course.childcategory?.title"
                    class="meta-category"
                  >
                    <i class="fas fa-folder"></i>
                    {{ course.childcategory.title }}
                  </span>
                </div>
                <div class="card-footer">
                  <div class="card-price">
                    <template v-if="course.is_purchased">
                      <span class="price-purchased">{{
                        $t("web_courses.enrolled") || "مسجل"
                      }}</span>
                    </template>
                    <template v-else-if="!course.price || course.price === 0">
                      <span class="price-free">{{
                        $t("course.free") || "مجاني"
                      }}</span>
                    </template>
                    <template v-else-if="course.discount_price">
                      <span class="price-current"
                        >{{ course.discount_price }} {{ $appCurrency }}</span
                      >
                      <span class="price-old"
                        >{{ course.price }} {{ $appCurrency }}</span
                      >
                    </template>
                    <template v-else>
                      <span class="price-current"
                        >{{ course.price }} {{ $appCurrency }}</span
                      >
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="empty-icon"><i class="fas fa-search"></i></div>
            <h3>
              {{ $t("web_courses.no_search_results") || "لا توجد نتائج للبحث" }}
            </h3>
            <p>
              {{
                $t("web_courses.try_different_search") || "جرب كلمات بحث مختلفة"
              }}
            </p>
          </div>

          <!-- Pagination -->
          <div v-if="globalSearchTotalPages > 1" class="pagination-container">
            <button
              class="pagination-btn"
              :disabled="globalSearchCurrentPage === 1"
              @click="changeGlobalSearchPage(globalSearchCurrentPage - 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <span class="page-info"
              >{{ globalSearchCurrentPage }} /
              {{ globalSearchTotalPages }}</span
            >
            <button
              class="pagination-btn"
              :disabled="globalSearchCurrentPage === globalSearchTotalPages"
              @click="changeGlobalSearchPage(globalSearchCurrentPage + 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
        </div>
      </section>

      <!-- Stats Bar (hidden when showing search results) -->
      <section v-if="!showGlobalSearchResults" class="stats-bar">
        <div class="stats-container">
          <div class="stat-item">
            <div class="stat-icon"><i class="fas fa-graduation-cap"></i></div>
            <div class="stat-content">
              <span class="stat-number">{{ totalCourses }}+</span>
              <span class="stat-label">{{
                $t("web_courses.total_courses") || "دورة"
              }}</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon"><i class="fas fa-layer-group"></i></div>
            <div class="stat-content">
              <span class="stat-number">{{ categories.length }}</span>
              <span class="stat-label">{{
                $t("web_courses.specialties") || "تخصص"
              }}</span>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">
              <i class="fas fa-chalkboard-teacher"></i>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ totalInstructors }}+</span>
              <span class="stat-label">{{
                $t("web_courses.instructors") || "محاضر"
              }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Categories Section (hidden when showing search results) -->
      <section v-if="!showGlobalSearchResults" class="categories-section">
        <div class="section-container">
          <div class="section-header">
            <h2 class="section-title">
              {{ $t("web_courses.all_specialties") || "جميع التخصصات" }}
            </h2>
            <p class="section-subtitle">
              {{ $t("web_courses.choose_specialty") || "اختر التخصص المناسب" }}
            </p>
          </div>

          <div v-if="isLoadingCategories" class="loading-state">
            <div class="loading-grid">
              <div v-for="n in 6" :key="n" class="skeleton-card"></div>
            </div>
          </div>

          <div v-else class="categories-grid">
            <div
              v-for="category in filteredCategories"
              :key="category.id"
              class="category-card"
              :class="{ expanded: expandedCategories.includes(category.id) }"
            >
              <div class="card-header" @click="toggleCategory(category.id)">
                <div class="card-icon">
                  <img
                    src="/assets/img/acad-icon.png"
                    alt=""
                    class="icon-image"
                  />
                </div>
                <div class="card-info">
                  <h3 class="card-title">
                    {{ category.title?.en || category.title }}
                  </h3>
                  <div class="card-meta">
                    <span class="meta-item"
                      ><i class="fas fa-folder"></i>
                      {{ category.subcategory?.length || 0 }}
                      {{ $t("web_courses.subcategories") || "فرعي" }}</span
                    >
                  </div>
                </div>
                <button
                  class="expand-button"
                  :class="{ rotated: expandedCategories.includes(category.id) }"
                >
                  <i class="fas fa-chevron-down"></i>
                </button>
              </div>

              <div
                class="subcategories-panel"
                :class="{ show: expandedCategories.includes(category.id) }"
              >
                <div
                  v-for="subcategory in category.subcategory"
                  :key="subcategory.id"
                  class="subcategory-block"
                >
                  <div
                    class="subcategory-header"
                    @click="toggleSubcategory(subcategory.id)"
                  >
                    <div class="subcategory-info">
                      <h4 class="subcategory-title">{{ subcategory.title }}</h4>
                      <span
                        v-if="subcategory.childcategory?.length"
                        class="child-count"
                        >{{ subcategory.childcategory.length }}</span
                      >
                    </div>
                    <div class="subcategory-actions">
                      <button
                        class="view-all-btn"
                        @click.stop="navigateToSubcategory(subcategory)"
                      >
                        {{ $t("web_courses.view_courses") || "عرض" }}
                        <i class="fas fa-arrow-left"></i>
                      </button>
                      <button
                        v-if="subcategory.childcategory?.length"
                        class="toggle-btn"
                        :class="{
                          rotated: expandedSubcategories.includes(
                            subcategory.id
                          ),
                        }"
                      >
                        <i class="fas fa-chevron-down"></i>
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="subcategory.childcategory?.length"
                    class="child-categories"
                    :class="{
                      show: expandedSubcategories.includes(subcategory.id),
                    }"
                  >
                    <button
                      v-for="child in subcategory.childcategory"
                      :key="child.id"
                      class="child-tag"
                      @click="selectChildCategory(child, subcategory)"
                    >
                      {{ child.title }} <i class="fas fa-arrow-left"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!isLoadingCategories && filteredCategories.length === 0"
            class="empty-state"
          >
            <div class="empty-icon"><i class="fas fa-search"></i></div>
            <h3>{{ $t("web_courses.no_results") || "لا توجد نتائج" }}</h3>
          </div>
        </div>
      </section>
    </div>

    <!-- ===== COURSES VIEW ===== -->
    <div v-else class="courses-view">
      <div class="breadcrumb-section">
        <div class="breadcrumb-container">
          <nav class="breadcrumb">
            <a
              href="#"
              class="breadcrumb-link"
              @click.prevent="goBackToCategories"
              ><i class="fas fa-home"></i>
              {{ $t("web_courses.specialties") || "التخصصات" }}</a
            >
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{
              selectedChildCategory.title
            }}</span>
          </nav>
          <button class="cart-button" @click="navigateToCart">
            <i class="fas fa-shopping-cart"></i>
            {{ $t("web_courses.cart") || "السلة" }}
          </button>
        </div>
      </div>

      <section class="page-header">
        <div class="header-container">
          <h1 class="page-title">{{ selectedChildCategory.title }}</h1>
          <p class="page-subtitle">
            {{ $t("web_courses.browse_courses_in") || "تصفح الدورات في" }}
            {{ selectedChildCategory.title }}
          </p>
        </div>
      </section>

      <section class="filters-section">
        <div class="filters-container">
          <div class="tab-filters">
            <button
              class="tab-button"
              :class="{ active: activeTab === 'recorded' }"
              @click="switchTab('recorded')"
            >
              <i class="fas fa-play-circle"></i>
              {{ $t("web_courses.recorded_courses") || "المسجلة" }}
            </button>
            <button
              class="tab-button"
              :class="{ active: activeTab === 'live' }"
              @click="switchTab('live')"
            >
              <i class="fas fa-broadcast-tower"></i>
              {{ $t("web_courses.live_courses") || "المباشر" }}
            </button>
            <button
              class="tab-button"
              :class="{ active: activeTab === 'bundles' }"
              @click="switchTab('bundles')"
            >
              <i class="fas fa-box"></i>
              {{ $t("web_courses.bundles") || "الباقات" }}
            </button>
          </div>
          <div class="search-filter">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                v-model="searchText"
                type="text"
                :placeholder="$t('web_courses.search_courses') || 'ابحث...'"
                @input="debouncedSearch"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="courses-section">
        <div class="courses-container">
          <div class="results-header">
            <span class="results-count"
              >{{ displayItems.length }}
              {{ $t("web_courses.results") || "نتيجة" }}</span
            >
            <!-- Custom Bundle Button -->
            <button
              v-if="isCustomizable && activeTab !== 'bundles'"
              class="create-bundle-btn"
              data-bs-toggle="modal"
              data-bs-target="#customBundleModal"
              @click="openCustomBundleModal"
            >
              <i class="fas fa-plus-circle"></i>
              <span>{{
                $t("category_courses.make_custom_bundle") || "أنشئ باقتك"
              }}</span>
            </button>
          </div>

          <div v-if="isLoadingCourses" class="loading-state">
            <div class="courses-grid">
              <div v-for="n in 6" :key="n" class="skeleton-course-card"></div>
            </div>
          </div>

          <div v-else-if="displayItems.length > 0" class="courses-grid">
            <div
              v-for="item in displayItems"
              :key="item.id"
              class="course-card"
            >
              <div class="card-image-wrapper" @click="navigateToItem(item)">
                <img
                  :src="item.image || item.preview_image || defaultImage"
                  @error="setDefaultImage"
                  alt=""
                  class="card-image"
                />
                <div class="card-badges">
                  <span v-if="item.is_live === '1'" class="badge live"
                    ><span class="live-dot"></span>
                    {{ $t("web_courses.live") || "مباشر" }}</span
                  >
                </div>
              </div>
              <div class="card-content" @click="navigateToItem(item)">
                <!-- Category/Specialty Badge -->
                <div class="card-category-row">
                  <div
                    v-if="
                      item.institute ||
                      item.category_name ||
                      selectedChildCategory
                    "
                    class="card-category"
                  >
                    <i class="fas fa-bookmark"></i>
                    <span>{{
                      item.institute?.title ||
                      item.institute ||
                      item.category_name ||
                      selectedChildCategory?.title
                    }}</span>
                  </div>
                  <span v-if="item.subscription_plan_id" class="badge qbank">{{
                    $t("web_courses.qbank") || "QBank"
                  }}</span>
                </div>
                <h3 class="card-title">{{ item.title }}</h3>
                <p v-if="getInstructorName(item)" class="card-instructor">
                  <i class="fas fa-chalkboard-teacher"></i>
                  {{ getInstructorName(item) }}
                </p>

                <!-- Last Updated Date -->
                <div
                  v-if="item.updated_at || item.last_update"
                  class="card-meta-info"
                >
                  <span class="meta-date"
                    ><i class="fas fa-clock"></i>
                    {{ $t("web_courses.last_updated") || "آخر تحديث" }}:
                    {{ formatDate(item.updated_at || item.last_update) }}</span
                  >
                </div>

                <!-- Progress for purchased courses -->
                <div v-if="item.is_purchased" class="card-progress">
                  <div class="progress-bar">
                    <div
                      class="progress-fill"
                      :style="{ width: `${item.progress || 0}%` }"
                    ></div>
                  </div>
                  <span class="progress-text"
                    >{{ item.progress || 0 }}%
                    {{ $t("web_courses.completed") || "مكتمل" }}</span
                  >
                </div>

                <!-- Price Section -->
                <div v-else class="card-price">
                  <span v-if="isFree(item)" class="price-free">{{
                    $t("web_courses.free") || "مجاني"
                  }}</span>
                  <template v-else-if="hasDiscount(item)">
                    <span class="price-old"
                      >{{ getOriginalPrice(item) }} {{ $appCurrency }}</span
                    >
                    <span class="price-current"
                      >{{ item.discount_price || item.price }} {{ $appCurrency }}</span
                    >
                    <span class="discount-badge"
                      >{{
                        calculateDiscountPercentage(item)
                      }}% {{ $t("web_courses.off") || "خصم" }}</span
                    >
                  </template>
                  <span v-else class="price-current"
                    >{{ item.price }} {{ $appCurrency }}</span
                  >
                </div>
              </div>

              <!-- Card Footer with Action Buttons -->
              <div class="card-footer">
                <button
                  class="view-course-btn"
                  @click="navigateToCourseDetails(item)"
                >
                  <i
                    :class="
                      item.is_purchased
                        ? 'fas fa-play-circle'
                        : 'fas fa-info-circle'
                    "
                  ></i>
                  {{
                    item.is_purchased
                      ? $t("web_courses.continue") || "متابعة"
                      : $t("web_courses.view_details") || "التفاصيل"
                  }}
                </button>
                <button
                  v-if="!item.is_purchased && getItemPrice(item) > 0"
                  class="add-to-cart-btn"
                  :class="{ 'in-cart': isInCart(item) }"
                  :disabled="loadingCart[item.id]"
                  @click.stop="addToCart(item)"
                >
                  <i
                    v-if="loadingCart[item.id]"
                    class="fas fa-spinner fa-spin"
                  ></i>
                  <i v-else-if="isInCart(item)" class="fas fa-times"></i>
                  <i v-else class="fas fa-cart-plus"></i>
                  <span v-if="!loadingCart[item.id]">{{
                    isInCart(item)
                      ? $t("web_courses.remove_from_cart") || "إزالة من السلة"
                      : $t("web_courses.add_to_cart") || "أضف للسلة"
                  }}</span>
                </button>
              </div>
            </div>
          </div>

          <div v-else class="empty-courses">
            <div class="empty-icon">
              <i
                :class="
                  activeTab === 'bundles'
                    ? 'fas fa-box-open'
                    : 'fas fa-book-open'
                "
              ></i>
            </div>
            <h3>
              {{
                activeTab === "bundles"
                  ? $t("web_courses.no_bundles") || "لا توجد باقات"
                  : $t("web_courses.no_courses") || "لا توجد دورات"
              }}
            </h3>
          </div>

          <div v-if="totalPages > 1" class="pagination">
            <button
              class="pagination-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button
              class="pagination-btn"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";
import Swal from "sweetalert2";

export default {
  name: "WebCourses",
  data() {
    return {
      categories: [],
      isLoadingCategories: true,
      expandedCategories: [],
      expandedSubcategories: [],
      categorySearchText: "",
      // Global Search Data
      globalSearchText: "",
      globalSearchQuery: "",
      globalSearchResults: [],
      isLoadingGlobalSearch: false,
      globalSearchCurrentPage: 1,
      globalSearchTotalPages: 1,
      showGlobalSearchResults: false,
      globalSearchDebounceTimer: null,
      selectedChildCategory: null,
      selectedSubcategory: null,
      selectedInstitute: null,
      activeTab: "recorded",
      searchText: "",
      currentSearchQuery: "",
      searchDebounceTimer: null,
      courses: [],
      bundles: [],
      isLoadingCourses: false,
      currentPage: 1,
      totalPages: 1,
      perPage: 12,
      loadingCart: {},
      cartCourses: [],
      cartBundles: [],
      totalCourses: 500,
      totalInstructors: 50,
      defaultImage: "/assets/img/acad-icon.png",
      token: localStorage.getItem("access_token"),
      // Custom Bundle Data
      customCourses: [],
      customCoursesCache: [],
      selectedBundleCourses: [],
      customCourseLinks: null,
      currentCustomPage: 1,
      isLoadingCustomCourses: false,
      isEnrollingBundle: false,
      bundleDiscounts: [],
      bundleMinCourses: 2,
      bundleMaxCourses: 5,
      isCustomizable: false,
      bundleStep: 1, // Step 1: Select courses, Step 2: Review & Buy
    };
  },
  computed: {
    displayItems() {
      return this.activeTab === "bundles" ? this.bundles : this.courses;
    },
    filteredCategories() {
      if (!this.categorySearchText) return this.categories;
      const search = this.categorySearchText.toLowerCase();
      return this.categories.filter((cat) =>
        (cat.title?.en || cat.title || "").toLowerCase().includes(search)
      );
    },
    // Custom Bundle Computed Properties
    bundleBasePrice() {
      return this.selectedBundleCourses.reduce((total, courseId) => {
        const course = this.customCoursesCache.find((c) => c.id === courseId);
        if (course) {
          return (
            total +
            (parseFloat(course.discount_price) || parseFloat(course.price) || 0)
          );
        }
        return total;
      }, 0);
    },
    bundleDiscount() {
      const count = this.selectedBundleCourses.length;
      if (!this.bundleDiscounts || this.bundleDiscounts.length === 0) return 0;
      const applicable = this.bundleDiscounts.find(
        (d) => parseInt(d.number_of_courses) === count
      );
      return applicable ? parseInt(applicable.discount) : 0;
    },
    bundleTotalPrice() {
      const discount = this.bundleDiscount / 100;
      return this.bundleBasePrice * (1 - discount);
    },
    selectedCoursesDetails() {
      return this.selectedBundleCourses
        .map((courseId) => {
          return this.customCoursesCache.find((c) => c.id === courseId);
        })
        .filter((c) => c);
    },
  },
  mounted() {
    this.initializeFromRoute();
    this.fetchCategories();
    this.loadCartItems();
  },
  methods: {
    initializeFromRoute() {
      const urlParams = new URLSearchParams(window.location.search);
      const childCategoryId = urlParams.get("childcategory_id");
      const subcategoryId = urlParams.get("subcategory_id");
      const instituteId = urlParams.get("institute_id");
      const title = urlParams.get("title");

      if (instituteId) {
        this.selectedInstitute = {
          id: parseInt(instituteId),
          title: title || "Institute Courses",
        };
        this.selectedChildCategory = {
          id: null,
          title: title || "Institute Courses",
        };
        this.fetchCourses();
      } else if (childCategoryId) {
        this.selectedChildCategory = {
          id: parseInt(childCategoryId),
          title: title || "Courses",
        };
        this.fetchCourses();
        this.checkCustomizableStatus(); // Check if customizable
      } else if (subcategoryId) {
        this.selectedChildCategory = {
          id: null,
          subcategory_id: parseInt(subcategoryId),
          title: title || "Courses",
        };
        this.fetchCourses();
        this.checkCustomizableStatus(); // Check if customizable
      }
    },
    async fetchCategories() {
      this.isLoadingCategories = true;
      try {
        const response = await fetch(`${apiUrl}/home/web`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
          },
        });
        const data = await response.json();
        this.categories = data.allcategory || [];
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        this.categories = [];
      } finally {
        this.isLoadingCategories = false;
      }
    },
    toggleCategory(categoryId) {
      const index = this.expandedCategories.indexOf(categoryId);
      if (index > -1) this.expandedCategories.splice(index, 1);
      else this.expandedCategories.push(categoryId);
    },
    // ===== Global Search Methods =====
    debouncedGlobalSearch() {
      clearTimeout(this.globalSearchDebounceTimer);
      this.globalSearchDebounceTimer = setTimeout(() => {
        this.performGlobalSearch();
      }, 500);
    },
    async performGlobalSearch() {
      if (!this.globalSearchText.trim()) {
        this.clearGlobalSearch();
        return;
      }

      this.globalSearchQuery = this.globalSearchText.trim();
      this.globalSearchCurrentPage = 1;
      this.showGlobalSearchResults = true;
      await this.fetchGlobalSearchResults();
    },
    async fetchGlobalSearchResults() {
      this.isLoadingGlobalSearch = true;

      const params = new URLSearchParams();
      params.append("page", this.globalSearchCurrentPage);
      params.append("search", this.globalSearchQuery);

      try {
        const response = await fetch(
          `${apiUrl}/courses/all?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-country-code": window.appRegion?.phone_code || "",
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        const result = await response.json();

        if (result.data) {
          this.globalSearchResults = result.data;
          if (result.meta) {
            this.globalSearchTotalPages = Math.ceil(
              result.meta.total / result.meta.per_page
            );
          } else if (result.last_page) {
            this.globalSearchTotalPages = result.last_page;
          } else {
            this.globalSearchTotalPages = 1;
          }
        } else if (Array.isArray(result)) {
          this.globalSearchResults = result;
          this.globalSearchTotalPages = 1;
        } else {
          this.globalSearchResults = [];
          this.globalSearchTotalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching global search results:", error);
        this.globalSearchResults = [];
        this.globalSearchTotalPages = 1;
      } finally {
        this.isLoadingGlobalSearch = false;
      }
    },
    changeGlobalSearchPage(page) {
      this.globalSearchCurrentPage = page;
      this.fetchGlobalSearchResults();
    },
    clearGlobalSearch() {
      this.globalSearchText = "";
      this.globalSearchQuery = "";
      this.globalSearchResults = [];
      this.globalSearchCurrentPage = 1;
      this.globalSearchTotalPages = 1;
      this.showGlobalSearchResults = false;
    },
    navigateToGlobalSearchCourse(course) {
      window.location.href = `/course-details?id=${course.id}`;
    },
    async addGlobalSearchCourseToCart(course) {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: this.$t("common.login_required") || "يرجى تسجيل الدخول",
          confirmButtonText: this.$t("common.ok") || "حسناً",
          confirmButtonColor: "#00c744",
        });
        return;
      }

      if (this.cartCourses.includes(course.id)) {
        Swal.fire({
          icon: "info",
          text:
            this.$t("cart.already_in_cart") ||
            "هذا العنصر موجود بالفعل في سلتك",
          showConfirmButton: false,
          timer: 1500,
        });
        return;
      }

      this.loadingCart[course.id] = true;
      const endpoint = `${apiUrl}/addtocart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`;

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ course_id: course.id }),
        });
        const result = await response.json();
        if (response.ok) {
          this.cartCourses.push(course.id);
          Swal.fire({
            icon: "success",
            text: this.$t("web_courses.course_added") || "تمت الإضافة للسلة",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          const errorMsg = (result.message || "").toLowerCase();
          if (
            errorMsg.includes("already in cart") ||
            errorMsg.includes("already exists")
          ) {
            if (!this.cartCourses.includes(course.id))
              this.cartCourses.push(course.id);
            Swal.fire({
              icon: "info",
              text:
                this.$t("cart.already_in_cart") ||
                "هذا العنصر موجود بالفعل في سلتك",
              showConfirmButton: false,
              timer: 1500,
            });
          } else {
            throw new Error(result.message || "Failed");
          }
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        Swal.fire({
          icon: "error",
          text: this.$t("web_courses.cart_error") || "فشل في الإضافة",
          showConfirmButton: false,
          timer: 1500,
        });
      } finally {
        this.loadingCart[course.id] = false;
      }
    },
    toggleSubcategory(subcategoryId) {
      const index = this.expandedSubcategories.indexOf(subcategoryId);
      if (index > -1) this.expandedSubcategories.splice(index, 1);
      else this.expandedSubcategories.push(subcategoryId);
    },
    selectChildCategory(childCategory, subcategory) {
      this.selectedChildCategory = childCategory;
      this.selectedSubcategory = subcategory;
      this.activeTab = "recorded";
      this.searchText = "";
      this.currentSearchQuery = "";
      this.currentPage = 1;
      const newUrl = `${window.location.pathname}?childcategory_id=${
        childCategory.id
      }&title=${encodeURIComponent(childCategory.title)}`;
      window.history.pushState({}, "", newUrl);
      this.fetchCourses();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    navigateToSubcategory(subcategory) {
      window.location.href = `/courses?subcategory_id=${
        subcategory.id
      }&title=${encodeURIComponent(subcategory.title)}`;
    },
    goBackToCategories() {
      this.selectedChildCategory = null;
      this.selectedSubcategory = null;
      this.selectedInstitute = null;
      this.courses = [];
      this.bundles = [];
      window.history.pushState({}, "", window.location.pathname);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    switchTab(tab) {
      this.activeTab = tab;
      this.searchText = "";
      this.currentSearchQuery = "";
      this.currentPage = 1;
      if (tab === "recorded") this.fetchCourses(false);
      else if (tab === "live") this.fetchCourses(true);
      else if (tab === "bundles") this.fetchBundles();
    },
    debouncedSearch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.performSearch();
      }, 500);
    },
    performSearch() {
      this.currentSearchQuery = this.searchText;
      this.currentPage = 1;
      if (this.activeTab === "bundles") this.fetchBundles();
      else this.fetchCourses(this.activeTab === "live");
    },
    async fetchCourses(isLive = false) {
      if (!this.selectedChildCategory && !this.selectedInstitute) return;
      this.isLoadingCourses = true;

      // Build query parameters (Flutter-style GET request)
      const params = new URLSearchParams();
      params.append("page", this.currentPage);
      params.append("is_live", isLive ? "1" : "0");

      if (this.selectedInstitute?.id) {
        params.append("institute_id", this.selectedInstitute.id);
      } else if (this.selectedChildCategory?.id) {
        params.append("childcategory_id", this.selectedChildCategory.id);
      } else if (this.selectedChildCategory?.subcategory_id) {
        params.append(
          "subcategory_id",
          this.selectedChildCategory.subcategory_id
        );
      }
      if (this.currentSearchQuery) {
        params.append("search", this.currentSearchQuery);
      }

      try {
        const response = await fetch(
          `${apiUrl}/courses/all?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-country-code": window.appRegion?.phone_code || "",
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        const result = await response.json();
        // Handle Flutter API response structure
        if (result.data) {
          this.courses = result.data;
          if (result.meta) {
            this.totalPages = Math.ceil(
              result.meta.total / result.meta.per_page
            );
          } else if (result.last_page) {
            this.totalPages = result.last_page;
          }
        } else if (Array.isArray(result)) {
          this.courses = result;
          this.totalPages = 1;
        } else {
          this.courses = [];
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.courses = [];
        this.totalPages = 1;
      } finally {
        this.isLoadingCourses = false;
      }
    },
    async fetchBundles() {
      if (!this.selectedChildCategory) return;
      this.isLoadingCourses = true;

      // Build query parameters (Flutter-style GET request)
      const params = new URLSearchParams();
      params.append("page", this.currentPage);

      if (this.selectedChildCategory.id) {
        params.append("childcategory_id", this.selectedChildCategory.id);
      } else if (this.selectedChildCategory.subcategory_id) {
        params.append(
          "subcategory_id",
          this.selectedChildCategory.subcategory_id
        );
      }
      if (this.currentSearchQuery) {
        params.append("search", this.currentSearchQuery);
      }

      try {
        const response = await fetch(`${apiUrl}/bundles?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
        });
        const result = await response.json();
        // Handle Flutter API response structure
        if (result.data) {
          this.bundles = result.data;
          if (result.meta) {
            this.totalPages = Math.ceil(
              result.meta.total / result.meta.per_page
            );
          } else if (result.last_page) {
            this.totalPages = result.last_page;
          }
        } else if (Array.isArray(result)) {
          this.bundles = result;
          this.totalPages = 1;
        } else {
          this.bundles = [];
          this.totalPages = 1;
        }
      } catch (error) {
        console.error("Error fetching bundles:", error);
        this.bundles = [];
        this.totalPages = 1;
      } finally {
        this.isLoadingCourses = false;
      }
    },
    changePage(page) {
      this.currentPage = page;
      if (this.activeTab === "bundles") this.fetchBundles();
      else this.fetchCourses(this.activeTab === "live");
      window.scrollTo({ top: 300, behavior: "smooth" });
    },
    async loadCartItems() {
      if (!this.token) return;
      try {
        const response = await fetch(
          `${apiUrl}/show/cart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        const data = await response.json();
        if (data.cart && Array.isArray(data.cart)) {
          this.cartCourses = data.cart
            .filter((item) => item.course_id !== null)
            .map((item) => item.course_id);
          this.cartBundles = data.cart
            .filter((item) => item.bundle_id !== null)
            .map((item) => item.bundle_id);
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    },
    async addToCart(item) {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: this.$t("common.login_required") || "يرجى تسجيل الدخول",
          confirmButtonText: this.$t("common.ok") || "حسناً",
          confirmButtonColor: "#00c744",
        });
        return;
      }

      const isBundle = this.activeTab === "bundles";
      const isInCart = this.isInCart(item);

      // If already in cart, remove it
      if (isInCart) {
        await this.removeFromCart(item);
        return;
      }

      // Otherwise add to cart
      this.loadingCart[item.id] = true;
      const endpoint = isBundle
        ? `${apiUrl}/addtocart/bundle?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`
        : `${apiUrl}/addtocart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`;
      const body = isBundle ? { bundle_id: item.id } : { course_id: item.id };
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (response.ok) {
          if (isBundle) this.cartBundles.push(item.id);
          else this.cartCourses.push(item.id);
          Swal.fire({
            icon: "success",
            text: isBundle
              ? this.$t("web_courses.bundle_added") || "تمت الإضافة للسلة"
              : this.$t("web_courses.course_added") || "تمت الإضافة للسلة",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          const errorMsg = (result.message || "").toLowerCase();
          if (
            errorMsg.includes("already in cart") ||
            errorMsg.includes("already exists")
          ) {
            if (isBundle && !this.cartBundles.includes(item.id))
              this.cartBundles.push(item.id);
            else if (!isBundle && !this.cartCourses.includes(item.id))
              this.cartCourses.push(item.id);
            Swal.fire({
              icon: "info",
              text:
                this.$t("cart.already_in_cart") ||
                "هذا العنصر موجود بالفعل في سلتك",
              showConfirmButton: false,
              timer: 1500,
            });
          } else {
            throw new Error(result.message || "Failed");
          }
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        Swal.fire({
          icon: "error",
          text: this.$t("web_courses.cart_error") || "فشل في الإضافة",
          showConfirmButton: false,
          timer: 1500,
        });
      } finally {
        this.loadingCart[item.id] = false;
      }
    },
    async removeFromCart(item) {
      const isBundle = this.activeTab === "bundles";
      this.loadingCart[item.id] = true;

      const endpoint = isBundle
        ? `${apiUrl}/remove/bundle?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`
        : `${apiUrl}/remove/cart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`;

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(
            isBundle ? { bundle_id: item.id } : { course_id: item.id }
          ),
        });

        if (response.ok) {
          if (isBundle) {
            this.cartBundles = this.cartBundles.filter((id) => id !== item.id);
          } else {
            this.cartCourses = this.cartCourses.filter((id) => id !== item.id);
          }
          Swal.fire({
            icon: "success",
            text: isBundle
              ? this.$t("web_courses.bundle_removed") || "تمت الإزالة من السلة"
              : this.$t("web_courses.course_removed") || "تمت الإزالة من السلة",
            showConfirmButton: false,
            timer: 1500,
          });
        } else throw new Error("Failed");
      } catch (error) {
        console.error("Error removing from cart:", error);
        Swal.fire({
          text: this.$t("web_courses.cart_remove_error") || "فشل في الإزالة",
          showConfirmButton: false,
          timer: 1500,
        });
      } finally {
        this.loadingCart[item.id] = false;
      }
    },
    isInCart(item) {
      return this.activeTab === "bundles"
        ? this.cartBundles.includes(item.id)
        : this.cartCourses.includes(item.id);
    },
    navigateToCart() {
      window.location.href = "/Cart";
    },
    navigateToItem(item) {
      if (this.activeTab === "bundles")
        window.location.href = `/bundle-details?id=${item.id}`;
      else window.location.href = `/course-details?id=${item.id}`;
    },
    navigateToCourseDetails(item) {
      if (this.activeTab === "bundles") {
        window.location.href = `/bundle-details?id=${item.id}`;
      } else {
        window.location.href = `/course-details?id=${item.id}`;
      }
    },
    getItemPrice(item) {
      return item.discount_price ?? item.price ?? 0;
    },
    getInstructorName(item) {
      if (item.user)
        return `${item.user.fname || ""} ${item.user.lname || ""}`.trim();
      return item.instructor_name || null;
    },
    isFree(item) {
      return item.discount_price === 0 || item.price === 0;
    },
    hasDiscount(item) {
      // For bundles: check if original_price exists and is different from current price
      if (item.original_price != null && item.original_price > 0) {
        const currentPrice = item.discount_price ?? item.price;
        return currentPrice != null && currentPrice < item.original_price;
      }
      // For courses: check if discount_price exists and is different from price
      return item.discount_price != null && item.discount_price !== item.price;
    },
    getOriginalPrice(item) {
      // For bundles, use original_price; for courses, use price
      return item.original_price || item.price;
    },
    calculateDiscountPercentage(item) {
      const originalPrice = this.getOriginalPrice(item);
      const currentPrice = item.discount_price ?? item.price;
      if (!originalPrice || originalPrice === 0) return 0;
      return Math.round((1 - currentPrice / originalPrice) * 100);
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString(
        this.$i18n?.locale || "ar",
        { year: "numeric", month: "short", day: "numeric" }
      );
    },
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },

    // Custom Bundle Methods
    openCustomBundleModal() {
      this.bundleStep = 1;
      if (this.customCourses.length === 0) {
        this.fetchCustomBundleCourses();
      }
    },
    goToStep2() {
      if (this.selectedBundleCourses.length >= this.bundleMinCourses) {
        this.bundleStep = 2;
      }
    },
    goToStep1() {
      this.bundleStep = 1;
    },
    resetBundleStep() {
      this.bundleStep = 1;
    },
    async fetchCustomBundleCourses(page = 1) {
      if (!this.selectedChildCategory) return;
      this.isLoadingCustomCourses = true;

      try {
        const data = { page };

        if (this.selectedChildCategory.id) {
          data.child_category_id = this.selectedChildCategory.id;
        }
        if (
          this.selectedSubcategory?.id ||
          this.selectedChildCategory.subcategory_id
        ) {
          data.sub_category_id =
            this.selectedSubcategory?.id ||
            this.selectedChildCategory.subcategory_id;
        }

        const response = await fetch(`${apiUrl}/web/get-customizable-courses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) throw new Error("Failed to fetch");

        const result = await response.json();

        if (result.courses?.data) {
          this.customCourses = result.courses.data;
          // Cache courses for price calculation
          result.courses.data.forEach((course) => {
            if (!this.customCoursesCache.some((c) => c.id === course.id)) {
              this.customCoursesCache.push(course);
            }
          });
          this.customCourseLinks = result.courses.meta;
          this.currentCustomPage = result.courses.meta?.current_page || 1;
        }

        if (result.discounts) {
          this.bundleDiscounts = result.discounts;
        }

        if (result.sub_category) {
          this.bundleMinCourses =
            parseInt(result.sub_category.min_courses) || 2;
          this.bundleMaxCourses =
            parseInt(result.sub_category.max_courses) || 5;
          this.isCustomizable = result.sub_category.customizable === "1";
        }
      } catch (error) {
        console.error("Error fetching custom bundle courses:", error);
        this.customCourses = [];
      } finally {
        this.isLoadingCustomCourses = false;
      }
    },
    addToBundleSelection(courseId) {
      if (this.selectedBundleCourses.includes(courseId)) return;
      if (this.selectedBundleCourses.length >= this.bundleMaxCourses) {
        Swal.fire({
          icon: "warning",
          title:
            this.$t("category_courses.max_courses_alert", {
              max: this.bundleMaxCourses,
            }) || `الحد الأقصى ${this.bundleMaxCourses} دورات`,
          confirmButtonText: this.$t("common.ok") || "موافق",
          confirmButtonColor: "#00c744",
        });
        return;
      }
      this.selectedBundleCourses.push(courseId);
    },
    removeFromBundleSelection(courseId) {
      this.selectedBundleCourses = this.selectedBundleCourses.filter(
        (id) => id !== courseId
      );
      // If in step 2 and courses fall below minimum, go back to step 1
      if (
        this.bundleStep === 2 &&
        this.selectedBundleCourses.length < this.bundleMinCourses
      ) {
        this.bundleStep = 1;
      }
    },
    async confirmBundleEnroll() {
      if (this.selectedBundleCourses.length < this.bundleMinCourses) {
        Swal.fire({
          icon: "warning",
          title:
            this.$t("category_courses.min_courses_alert", {
              min: this.bundleMinCourses,
            }) || `يجب اختيار ${this.bundleMinCourses} دورات على الأقل`,
          confirmButtonText: this.$t("common.ok") || "موافق",
          confirmButtonColor: "#00c744",
        });
        return;
      }

      const result = await Swal.fire({
        title:
          this.$t("category_courses.confirm_enroll") ||
          "هل أنت متأكد من الاشتراك؟",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("common.yes") || "نعم",
        cancelButtonText: this.$t("common.cancel") || "إلغاء",
        confirmButtonColor: "#00c744",
      });

      if (result.isConfirmed) {
        this.enrollInBundle();
      }
    },
    async enrollInBundle() {
      this.isEnrollingBundle = true;

      try {
        const enrollmentData = {
          courses_ids: this.selectedBundleCourses,
        };

        if (this.selectedChildCategory?.id) {
          enrollmentData.childcategory_id = this.selectedChildCategory.id;
        }
        if (
          this.selectedSubcategory?.id ||
          this.selectedChildCategory?.subcategory_id
        ) {
          enrollmentData.subcategory_id =
            this.selectedSubcategory?.id ||
            this.selectedChildCategory.subcategory_id;
        }

        const response = await fetch(`${apiUrl}/custom-bundles/enroll`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(enrollmentData),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || "فشل عملية الاشتراك");
        }

        Swal.fire({
          icon: "success",
          title:
            this.$t("category_courses.enroll_success") || "تم الشراء بنجاح",
          text: this.$t("category_courses.good_luck") || "نتمنى لك التوفيق",
          confirmButtonColor: "#00c744",
        }).then(() => {
          // Close modal and reset
          const modal = bootstrap.Modal.getInstance(
            this.$refs.customBundleModal
          );
          if (modal) modal.hide();
          this.selectedBundleCourses = [];
          this.fetchCourses();
        });
      } catch (error) {
        console.error("Error enrolling:", error);
        Swal.fire({
          icon: "error",
          title:
            error.message ||
            this.$t("category_courses.enroll_error") ||
            "فشل عملية الاشتراك",
          confirmButtonText: this.$t("common.ok") || "موافق",
          confirmButtonColor: "#00c744",
        });
      } finally {
        this.isEnrollingBundle = false;
      }
    },
    checkCustomizable() {
      // Check if current subcategory is customizable when fetching courses
      if (this.selectedSubcategory?.customizable === "1") {
        this.isCustomizable = true;
        this.bundleMinCourses =
          parseInt(this.selectedSubcategory.min_courses) || 2;
        this.bundleMaxCourses =
          parseInt(this.selectedSubcategory.max_courses) || 5;
      }
    },
    async checkCustomizableStatus() {
      // Call API to check if the current category/subcategory supports custom bundles
      if (!this.selectedChildCategory) return;

      try {
        const data = {};

        if (this.selectedChildCategory.id) {
          data.child_category_id = this.selectedChildCategory.id;
        }
        if (this.selectedChildCategory.subcategory_id) {
          data.sub_category_id = this.selectedChildCategory.subcategory_id;
        }

        const response = await fetch(`${apiUrl}/web/get-customizable-courses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          this.isCustomizable = false;
          return;
        }

        const result = await response.json();

        if (result.sub_category) {
          this.isCustomizable = result.sub_category.customizable === "1";
          this.bundleMinCourses =
            parseInt(result.sub_category.min_courses) || 2;
          this.bundleMaxCourses =
            parseInt(result.sub_category.max_courses) || 5;
        }

        if (result.discounts) {
          this.bundleDiscounts = result.discounts;
        }

        // Pre-cache courses data
        if (result.courses?.data) {
          this.customCourses = result.courses.data;
          result.courses.data.forEach((course) => {
            if (!this.customCoursesCache.some((c) => c.id === course.id)) {
              this.customCoursesCache.push(course);
            }
          });
          this.customCourseLinks = result.courses.meta;
          this.currentCustomPage = result.courses.meta?.current_page || 1;
        }
      } catch (error) {
        console.error("Error checking customizable status:", error);
        this.isCustomizable = false;
      }
    },
  },
};
</script>

<style scoped>
/* Icon Color Override - Fix external CSS gray icons */
.web-courses-page i.bi,
.web-courses-page i[class^="fonticon-"],
.web-courses-page i[class*=" fonticon-"],
.web-courses-page i[class^="fa-"],
.web-courses-page i[class*=" fa-"],
.web-courses-page i[class^="la-"],
.web-courses-page i[class*=" la-"],
.web-courses-page i.fas,
.web-courses-page i.far,
.web-courses-page i.fab,
.web-courses-page i.fal {
  color: inherit;
}
.hero-section i,
.add-to-cart-btn i,
.cart-button i,
.view-all-btn i {
  color: #ffffff !important;
}

/* Base */
.web-courses-page {
  min-height: 100vh;
  background: #f8fafc;
  direction: rtl;
  font-family: "Cairo", "Segoe UI", Tahoma, sans-serif;
}

/* Hero */
.hero-section {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  padding: 80px 24px;
  position: relative;
  overflow: hidden;
}
.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}
.hero-content {
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
}
.hero-title {
  color: white;
  font-size: 3rem;
  font-weight: 800;
  margin: 0 0 16px;
}
.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  margin: 0 0 32px;
}
.hero-search {
  max-width: 600px;
  margin: 0 auto;
}
.search-wrapper {
  position: relative;
}
.search-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}
.hero-search .search-input {
  width: 100%;
  padding: 18px 50px 18px 50px;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  outline: none;
}
.search-clear-btn {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: #e2e8f0;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}
.search-clear-btn:hover {
  background: #cbd5e1;
  color: #334155;
}
.hero-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}
.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}
.circle-1 {
  width: 400px;
  height: 400px;
  top: -150px;
  right: -100px;
  animation: float 6s ease-in-out infinite;
}
.circle-2 {
  width: 300px;
  height: 300px;
  bottom: -100px;
  left: 5%;
  animation: float 8s ease-in-out infinite 1s;
}
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Stats */
.stats-bar {
  background: white;
  padding: 24px;
  margin-top: -40px;
  position: relative;
  z-index: 10;
  border-radius: 24px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}
.stats-container {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 24px;
}
.stat-item {
  display: flex;
  align-items: center;
  gap: 16px;
}
.stat-icon {
  width: 56px;
  height: 56px;
  background: #e8fff1;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-icon i {
  font-size: 24px;
  color: #00c744 !important;
}
.stat-content {
  display: flex;
  flex-direction: column;
}
.stat-number {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a1a2e;
}
.stat-label {
  font-size: 0.875rem;
  color: #64748b;
}

/* Global Search Results Section */
.global-search-results-section {
  padding: 60px 24px;
  background: #f8fafc;
}
.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}
.search-results-header .section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
}
.search-results-header .section-title i {
  color: #00c744 !important;
}
.search-query-text {
  color: #00c744;
  font-weight: 600;
}
.back-to-categories-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  color: #64748b;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.back-to-categories-btn:hover {
  border-color: #00c744;
  color: #00c744;
}
.global-search-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}
.global-search-grid .course-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}
.global-search-grid .course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.global-search-grid .card-image {
  position: relative;
  height: 180px;
  overflow: hidden;
}
.global-search-grid .card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.global-search-grid .purchased-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #00c744;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}
.global-search-grid .card-actions {
  position: absolute;
  bottom: 12px;
  left: 12px;
}
.global-search-grid .cart-icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}
.global-search-grid .cart-icon-btn i {
  color: #00c744 !important;
  font-size: 16px;
}
.global-search-grid .cart-icon-btn.in-cart {
  background: #00c744;
}
.global-search-grid .cart-icon-btn.in-cart i {
  color: white !important;
}
.global-search-grid .cart-icon-btn:hover {
  transform: scale(1.1);
}
.global-search-grid .card-content {
  padding: 16px;
}
.global-search-grid .card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.global-search-grid .card-instructor {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 8px;
}
.global-search-grid .card-meta {
  margin-bottom: 12px;
}
.global-search-grid .meta-category {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 20px;
}
.global-search-grid .meta-category i {
  color: #94a3b8 !important;
}
.global-search-grid .card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.global-search-grid .card-price {
  display: flex;
  align-items: center;
  gap: 8px;
}
.global-search-grid .price-current {
  font-size: 1.125rem;
  font-weight: 800;
  color: #00c744;
}
.global-search-grid .price-old {
  font-size: 0.875rem;
  color: #94a3b8;
  text-decoration: line-through;
}
.global-search-grid .price-free {
  font-size: 1rem;
  font-weight: 700;
  color: #00c744;
}
.global-search-grid .price-purchased {
  font-size: 0.875rem;
  font-weight: 600;
  color: #00c744;
}
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 40px;
}
.pagination-container .pagination-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: white;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-container .pagination-btn:hover:not(:disabled) {
  border-color: #00c744;
  color: #00c744;
}
.pagination-container .pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-container .page-info {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 600;
}

/* Categories Section */
.categories-section {
  padding: 60px 24px;
}
.section-container {
  max-width: 1400px;
  margin: 0 auto;
}
.section-header {
  text-align: center;
  margin-bottom: 48px;
}
.section-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 12px;
}
.section-subtitle {
  font-size: 1.1rem;
  color: #64748b;
  margin: 0;
}
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
}
.category-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}
.category-card:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  border-color: #e8fff1;
  transform: translateY(-4px);
}
.card-header {
  display: flex;
  align-items: center;
  padding: 24px;
  cursor: pointer;
  gap: 16px;
  transition: all 0.3s ease;
}
.card-header:hover {
  background: #f8fafc;
}
.card-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.icon-image {
  width: 36px;
  height: 36px;
  object-fit: contain;
}
.card-info {
  flex: 1;
  min-width: 0;
}
.card-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
}
.card-meta {
  display: flex;
  align-items: center;
  gap: 16px;
}
.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #64748b;
}
.meta-item i {
  color: #00c744;
}
.expand-button {
  width: 40px;
  height: 40px;
  background: #f8fafc;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #00c744;
}
.expand-button:hover {
  background: #e8fff1;
}
.expand-button.rotated {
  transform: rotate(180deg);
}
.subcategories-panel {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
  background: #f8fafc;
}
.subcategories-panel.show {
  max-height: 2000px;
}
.subcategory-block {
  border-top: 1px solid #e2e8f0;
}
.subcategory-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.subcategory-header:hover {
  background: white;
}
.subcategory-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.subcategory-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}
.child-count {
  font-size: 0.75rem;
  color: #94a3b8;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 20px;
}
.subcategory-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.view-all-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #00c744;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.view-all-btn:hover {
  background: #00a83b;
  transform: translateX(-4px);
}
.toggle-btn {
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #64748b;
}
.toggle-btn:hover {
  border-color: #00c744;
  color: #00c744;
}
.toggle-btn.rotated {
  transform: rotate(180deg);
}
.child-categories {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding: 0 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.child-categories.show {
  max-height: 500px;
  padding: 0 24px 16px;
}
.child-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 25px;
  font-size: 0.875rem;
  color: #1a1a2e;
  cursor: pointer;
  transition: all 0.3s ease;
}
.child-tag:hover {
  border-color: #00c744;
  background: #e8fff1;
  color: #00c744;
  transform: translateX(-4px);
}
.child-tag i {
  font-size: 12px;
  opacity: 0;
  transition: all 0.3s ease;
}
.child-tag:hover i {
  opacity: 1;
}

/* Courses View */
.breadcrumb-section {
  background: white;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
}
.breadcrumb-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}
.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #00c744;
  text-decoration: none;
  transition: all 0.3s ease;
}
.breadcrumb-link:hover {
  color: #00a83b;
}
.breadcrumb-separator {
  color: #94a3b8;
}
.breadcrumb-current {
  color: #64748b;
}
.cart-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #00c744;
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.cart-button:hover {
  background: #00a83b;
  transform: scale(1.05);
}
.page-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 48px 24px;
  text-align: center;
}
.header-container {
  max-width: 1400px;
  margin: 0 auto;
}
.page-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0 0 8px;
}
.page-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin: 0;
}
.filters-section {
  background: white;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 50;
}
.filters-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.tab-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f8fafc;
  border: 2px solid transparent;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}
.tab-button:hover {
  border-color: #e8fff1;
  color: #00c744;
}
.tab-button.active {
  background: #00c744;
  color: white;
  border-color: #00c744;
}
.search-filter {
  flex: 1;
  max-width: 400px;
}
.search-box {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 25px;
  padding: 4px 4px 4px 16px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}
.search-box:focus-within {
  border-color: #00c744;
  background: white;
}
.search-box i {
  color: #94a3b8;
  margin-left: 8px;
}
.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px;
  font-size: 0.9rem;
  outline: none;
}
.search-btn {
  padding: 10px 20px;
  background: #00c744;
  color: white;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.search-btn:hover {
  background: #00a83b;
}
.courses-section {
  padding: 40px 24px;
}
.courses-container {
  max-width: 1400px;
  margin: 0 auto;
}
.results-header {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.results-count {
  font-size: 0.9rem;
  color: #64748b;
}

/* Create Bundle Button */
.create-bundle-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}
.create-bundle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.4);
}
.create-bundle-btn i {
  font-size: 1.1rem;
}
.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}
.course-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}
.course-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.card-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}
.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}
.course-card:hover .card-image {
  transform: scale(1.05);
}
.card-badges {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge.live {
  background: #ef4444;
  color: white;
}
.live-dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  animation: pulse 1s ease-in-out infinite;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
.badge.qbank {
  background: #3b82f6;
  color: white;
}
.card-cart-btn {
  position: absolute;
  bottom: 12px;
  left: 12px;
  width: 44px;
  height: 44px;
  background: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  color: #00c744;
  font-size: 18px;
}
.card-cart-btn:hover:not(:disabled) {
  background: #00c744;
  color: white;
  transform: scale(1.1);
}
.card-cart-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.card-content {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}
.card-category-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.card-category-row .badge.qbank {
  position: static;
  font-size: 0.7rem;
  padding: 5px 10px;
  border-radius: 20px;
}
.card-category {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #00c744;
  margin-bottom: 0;
  padding: 6px 12px;
  background: #e8fff1;
  border-radius: 20px;
  width: fit-content;
}
.card-category i {
  font-size: 10px;
}
.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.card-instructor {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 10px;
}
.card-instructor i {
  color: #00c744;
  font-size: 14px;
}
.card-meta-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.meta-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #94a3b8;
}
.meta-date i {
  font-size: 12px;
  color: #00c744;
}
.card-progress {
  margin-top: auto;
  padding-top: 12px;
}
.progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c744, #00a83b);
  transition: width 0.3s ease;
  border-radius: 4px;
}
.progress-text {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}
.card-price {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 12px;
  flex-wrap: wrap;
}
.price-free {
  font-size: 1.2rem;
  font-weight: 700;
  color: #00c744;
  background: #e8fff1;
  padding: 4px 12px;
  border-radius: 8px;
}
.price-current {
  font-size: 1.2rem;
  font-weight: 800;
  color: #00c744;
}
.price-old {
  font-size: 0.9rem;
  color: #94a3b8;
  text-decoration: line-through;
}
.discount-badge {
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  background: #ef4444;
  padding: 4px 10px;
  border-radius: 20px;
}
.card-footer {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 12px;
}
.view-course-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Cairo", sans-serif;
}
.view-course-btn:hover {
  background: #00c744;
  color: white;
  border-color: #00c744;
}
.view-course-btn i {
  font-size: 16px;
}
.add-to-cart-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
  font-family: "Cairo", sans-serif;
}
.add-to-cart-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.4);
  background: linear-gradient(135deg, #00a83b 0%, #008f32 100%);
}
.add-to-cart-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}
.add-to-cart-btn.in-cart {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}
.add-to-cart-btn.in-cart:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}
.add-to-cart-btn i {
  font-size: 16px;
}
.empty-state,
.empty-courses {
  text-align: center;
  padding: 80px 24px;
}
.empty-icon {
  width: 100px;
  height: 100px;
  background: #e8fff1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}
.empty-icon i {
  font-size: 40px;
  color: #00c744;
}
.empty-state h3,
.empty-courses h3 {
  font-size: 1.25rem;
  color: #1a1a2e;
  margin: 0 0 8px;
}
.loading-state {
  padding: 40px 0;
}
.loading-grid,
.skeleton-course-card {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}
.skeleton-card,
.skeleton-course-card {
  background: white;
  border-radius: 24px;
  height: 200px;
  animation: shimmer 1.5s infinite;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 0;
}
.pagination-btn {
  width: 44px;
  height: 44px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c744;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-btn:hover:not(:disabled) {
  background: #00c744;
  color: white;
  border-color: #00c744;
}
.pagination-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.page-info {
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
}

/* Responsive */
@media (max-width: 1024px) {
  .categories-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
  .hero-title {
    font-size: 2.5rem;
  }
}
@media (max-width: 768px) {
  .hero-section {
    padding: 60px 16px;
  }
  .hero-title {
    font-size: 2rem;
  }
  .hero-subtitle {
    font-size: 1rem;
  }
  .stats-bar {
    margin: -30px 16px 0;
    padding: 20px;
  }
  .stats-container {
    flex-direction: column;
    gap: 16px;
  }
  .categories-section {
    padding: 40px 16px;
  }
  .categories-grid {
    grid-template-columns: 1fr;
  }
  .courses-grid {
    grid-template-columns: 1fr;
  }
  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }
  .tab-filters {
    justify-content: center;
  }
  .search-filter {
    max-width: 100%;
  }
  .page-title {
    font-size: 1.8rem;
  }
  .breadcrumb-container {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  .card-footer {
    flex-direction: column;
    gap: 10px;
  }
  .view-course-btn,
  .add-to-cart-btn {
    width: 100%;
    padding: 12px;
  }
  .card-price {
    flex-direction: row;
    justify-content: flex-start;
  }
  .card-category {
    font-size: 0.7rem;
    padding: 5px 10px;
  }
  .card-category-row {
    gap: 6px;
  }
  .card-category-row .badge.qbank {
    font-size: 0.65rem;
    padding: 4px 8px;
  }
}
@media (max-width: 480px) {
  .tab-button {
    padding: 10px 16px;
    font-size: 0.8rem;
  }
  .card-title {
    font-size: 1rem;
  }
  .price-current {
    font-size: 1.1rem;
  }
  .add-to-cart-btn span,
  .view-course-btn span {
    font-size: 0.85rem;
  }
}

/* Dark Mode */
[data-bs-theme="dark"] .web-courses-page,
[data-theme="dark"] .web-courses-page,
.dark .web-courses-page {
  background: #0f172a;
}
[data-bs-theme="dark"] .stats-bar,
[data-theme="dark"] .stats-bar,
.dark .stats-bar,
[data-bs-theme="dark"] .category-card,
[data-theme="dark"] .category-card,
.dark .category-card,
[data-bs-theme="dark"] .course-card,
[data-theme="dark"] .course-card,
.dark .course-card,
[data-bs-theme="dark"] .breadcrumb-section,
[data-theme="dark"] .breadcrumb-section,
.dark .breadcrumb-section,
[data-bs-theme="dark"] .filters-section,
[data-theme="dark"] .filters-section,
.dark .filters-section {
  background: #1e293b;
}
[data-bs-theme="dark"] .card-header:hover,
[data-theme="dark"] .card-header:hover,
.dark .card-header:hover,
[data-bs-theme="dark"] .subcategories-panel,
[data-theme="dark"] .subcategories-panel,
.dark .subcategories-panel {
  background: #0f172a;
}
[data-bs-theme="dark"] .card-title,
[data-theme="dark"] .card-title,
.dark .card-title,
[data-bs-theme="dark"] .section-title,
[data-theme="dark"] .section-title,
.dark .section-title,
[data-bs-theme="dark"] .stat-number,
[data-theme="dark"] .stat-number,
.dark .stat-number,
[data-bs-theme="dark"] .subcategory-title,
[data-theme="dark"] .subcategory-title,
.dark .subcategory-title {
  color: #f1f5f9;
}
[data-bs-theme="dark"] .section-subtitle,
[data-theme="dark"] .section-subtitle,
.dark .section-subtitle,
[data-bs-theme="dark"] .stat-label,
[data-theme="dark"] .stat-label,
.dark .stat-label,
[data-bs-theme="dark"] .card-instructor,
[data-theme="dark"] .card-instructor,
.dark .card-instructor {
  color: #94a3b8;
}
[data-bs-theme="dark"] .stat-icon,
[data-theme="dark"] .stat-icon,
.dark .stat-icon,
[data-bs-theme="dark"] .card-icon,
[data-theme="dark"] .card-icon,
.dark .card-icon {
  background: #1a3a2a;
}
[data-bs-theme="dark"] .expand-button,
[data-theme="dark"] .expand-button,
.dark .expand-button,
[data-bs-theme="dark"] .tab-button,
[data-theme="dark"] .tab-button,
.dark .tab-button,
[data-bs-theme="dark"] .search-box,
[data-theme="dark"] .search-box,
.dark .search-box,
[data-bs-theme="dark"] .view-course-btn,
[data-theme="dark"] .view-course-btn,
.dark .view-course-btn {
  background: #0f172a;
}
[data-bs-theme="dark"] .child-tag,
[data-theme="dark"] .child-tag,
.dark .child-tag,
[data-bs-theme="dark"] .toggle-btn,
[data-theme="dark"] .toggle-btn,
.dark .toggle-btn {
  background: #1e293b;
  border-color: #334155;
  color: #f1f5f9;
}
[data-bs-theme="dark"] .subcategory-header:hover,
[data-theme="dark"] .subcategory-header:hover,
.dark .subcategory-header:hover {
  background: #1e293b;
}
[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: #1a3a2a;
}
[data-bs-theme="dark"] .search-box input,
[data-theme="dark"] .search-box input,
.dark .search-box input {
  color: #f1f5f9;
}
[data-bs-theme="dark"] .tab-button,
[data-theme="dark"] .tab-button,
.dark .tab-button {
  color: #94a3b8;
}
[data-bs-theme="dark"] .pagination-btn,
[data-theme="dark"] .pagination-btn,
.dark .pagination-btn {
  background: #1e293b;
  border-color: #334155;
}
[data-bs-theme="dark"] .global-search-results-section,
[data-theme="dark"] .global-search-results-section,
.dark .global-search-results-section {
  background: #0f172a;
}
[data-bs-theme="dark"] .global-search-grid .course-card,
[data-theme="dark"] .global-search-grid .course-card,
.dark .global-search-grid .course-card {
  background: #1e293b;
}
[data-bs-theme="dark"] .global-search-grid .card-title,
[data-theme="dark"] .global-search-grid .card-title,
.dark .global-search-grid .card-title {
  color: #f1f5f9;
}
[data-bs-theme="dark"] .global-search-grid .card-instructor,
[data-theme="dark"] .global-search-grid .card-instructor,
.dark .global-search-grid .card-instructor {
  color: #94a3b8;
}
[data-bs-theme="dark"] .global-search-grid .meta-category,
[data-theme="dark"] .global-search-grid .meta-category,
.dark .global-search-grid .meta-category {
  background: #0f172a;
}
[data-bs-theme="dark"] .back-to-categories-btn,
[data-theme="dark"] .back-to-categories-btn,
.dark .back-to-categories-btn {
  background: #1e293b;
  border-color: #334155;
  color: #94a3b8;
}
[data-bs-theme="dark"] .pagination-container .pagination-btn,
[data-theme="dark"] .pagination-container .pagination-btn,
.dark .pagination-container .pagination-btn {
  background: #1e293b;
  border-color: #334155;
}
</style>

<style>
/* Simple Toast Notification Styles (unscoped for SweetAlert) */
.web-courses-toast-simple {
  font-family: "Cairo", "Segoe UI", Tahoma, sans-serif !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  padding: 16px 20px !important;
  min-width: auto !important;
}
.web-courses-toast-simple .swal2-html-container {
  margin: 0 !important;
  padding: 0 !important;
}
.web-courses-toast-simple .swal2-timer-progress-bar {
  background: linear-gradient(90deg, #00c744, #00a83b) !important;
  height: 3px !important;
}

/* Custom Bundle Modal Styles */
#customBundleModal .modal-content.custom-bundle-modal {
  border: none;
  border-radius: 24px;
  overflow: hidden;
}
#customBundleModal .modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  padding: 20px 24px;
}
#customBundleModal .modal-title {
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
}
#customBundleModal .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}
#customBundleModal .btn-close:hover {
  opacity: 1;
}
#customBundleModal .modal-body {
  padding: 24px;
  background: #f8fafc;
  max-height: 60vh;
  overflow-y: scroll;
}
#customBundleModal .modal-body::-webkit-scrollbar {
  width: 12px !important;
  display: block !important;
}
#customBundleModal .modal-body::-webkit-scrollbar-track {
  background: #e8fff1 !important;
  border-radius: 6px !important;
}
#customBundleModal .modal-body::-webkit-scrollbar-thumb {
  background-color: #00c744 !important;
  border-radius: 6px !important;
  border: 2px solid #e8fff1 !important;
}
#customBundleModal .modal-body::-webkit-scrollbar-thumb:hover {
  background-color: #00a83b !important;
}
/* Firefox scrollbar */
#customBundleModal .modal-body {
  scrollbar-width: auto;
  scrollbar-color: #00c744 #e8fff1;
}
.custom-bundle-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
}
.custom-bundle-loading .spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.custom-bundle-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}
.custom-bundle-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  color: #cbd5e1;
}
.custom-bundle-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}
.custom-bundle-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.custom-bundle-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}
.custom-bundle-card.selected {
  border-color: #00c744;
  background: #f0fff4;
}
.bundle-card-image {
  height: 120px;
  overflow: hidden;
}
.bundle-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bundle-card-content {
  padding: 12px;
}
.bundle-card-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.bundle-card-instructor {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0 0 8px;
}
.bundle-card-price {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.bundle-card-price .price-current {
  font-size: 0.9rem;
  font-weight: 700;
  color: #00c744;
}
.bundle-card-price .price-old {
  font-size: 0.75rem;
  color: #94a3b8;
  text-decoration: line-through;
}
.bundle-card-price .price-free {
  font-size: 0.9rem;
  font-weight: 700;
  color: #00c744;
}
.bundle-add-btn,
.bundle-remove-btn {
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.bundle-add-btn {
  background: #00c744;
  color: white;
}
.bundle-add-btn:hover {
  background: #00a83b;
}
.bundle-remove-btn {
  background: #ef4444;
  color: white;
}
.bundle-remove-btn:hover {
  background: #dc2626;
}
.custom-bundle-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
.custom-bundle-pagination .pagination-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #64748b;
}
.custom-bundle-pagination .pagination-btn:hover:not(:disabled) {
  border-color: #00c744;
  color: #00c744;
}
.custom-bundle-pagination .pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.custom-bundle-pagination .page-info {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
}
#customBundleModal .modal-footer.custom-bundle-footer {
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 20px 24px;
  flex-direction: column;
  gap: 16px;
}
.bundle-summary {
  width: 100%;
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.9rem;
  color: #64748b;
}
.summary-row.discount {
  color: #00c744;
}
.summary-row.total {
  border-top: 1px solid #e2e8f0;
  padding-top: 12px;
  margin-top: 4px;
  font-weight: 700;
  color: #1a1a2e;
  font-size: 1rem;
}
.summary-value {
  font-weight: 700;
}
.bundle-checkout-btn {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.bundle-checkout-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 199, 68, 0.3);
}
.bundle-checkout-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.min-courses-hint {
  font-size: 0.85rem;
  color: #f59e0b;
  text-align: center;
  margin: 0;
}

/* Bundle Continue Button (Step 1) */
.bundle-continue-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}
.bundle-continue-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}
.bundle-continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Step 2: Review Body */
.bundle-review-body {
  padding: 20px 24px;
  background: #f8fafc;
}
.bundle-review-header {
  margin-bottom: 20px;
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.back-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Selected Courses List */
.selected-courses-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}
.selected-course-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.selected-course-image {
  width: 80px;
  height: 60px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
}
.selected-course-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.selected-course-info {
  flex: 1;
  min-width: 0;
}
.selected-course-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.selected-course-instructor {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
}
.selected-course-price {
  font-size: 1rem;
  font-weight: 700;
  color: #00c744;
  white-space: nowrap;
}
.remove-course-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fee2e2;
  color: #ef4444;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}
.remove-course-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

/* Order Summary */
.bundle-order-summary {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.order-summary-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
}
.order-summary-title i {
  color: #00c744;
}
.order-summary-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.order-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
  color: #475569;
}
.order-row.discount {
  color: #f59e0b;
}
.order-row.total {
  padding-top: 12px;
  border-top: 2px dashed #e2e8f0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #00c744;
}
</style>
